/*
  GEMDRAW — Integration adapter

  Single boundary between the storefront and whatever system eventually owns
  entries, campaign status and membership entitlement. No section, snippet or
  component may read that data from anywhere else.

  Contract:
    getProductEntryValue(productId)
    getCartLineEntryValue(lineKey)
    getCartTotalEntries()
    getCustomerEntryHistory()
    getGiveawayStatus(campaignId)
    getMembershipEntitlement()

  Every method resolves to a result envelope:
    { status: 'ok' | 'unavailable' | 'error', value, source, authoritative, reason }

  Rules this file enforces:
  - With no provider selected, every method resolves to an explicit unavailable
    state. It never guesses, and it never falls back to a number.
  - Entries are never derived from price, quantity or a multiplier written into
    display copy. There is deliberately no arithmetic of that shape anywhere in
    this file.
  - Values that come from theme metafields are presentational mirrors of the
    authoritative system and are always returned with authoritative: false.

  The provider is chosen in Theme settings. Selecting a real provider is a
  separate phase and requires the client to confirm the entry-management system.
*/

(function () {
  'use strict';

  const CONFIG_ELEMENT_ID = 'gd-adapter-config';

  function readConfig() {
    const element = document.getElementById(CONFIG_ELEMENT_ID);
    if (!element) return { provider: 'none' };

    try {
      return JSON.parse(element.textContent) || { provider: 'none' };
    } catch (error) {
      console.warn('[gemdraw] Adapter configuration could not be parsed.', error);
      return { provider: 'none' };
    }
  }

  function unavailable(reason) {
    return Promise.resolve({
      status: 'unavailable',
      value: null,
      source: null,
      authoritative: false,
      reason: reason || 'provider_not_configured',
    });
  }

  function mirrored(value, source) {
    if (value === null || value === undefined || value === '') {
      return unavailable('no_display_value');
    }

    return Promise.resolve({
      status: 'ok',
      value: value,
      source: source || 'theme_metafield',
      authoritative: false,
      reason: null,
    });
  }

  /*
    No provider connected. This is the default and the correct state until the
    client confirms the entry-management system.
  */
  const noProvider = {
    id: 'none',
    getProductEntryValue: function () {
      return unavailable();
    },
    getCartLineEntryValue: function () {
      return unavailable();
    },
    getCartTotalEntries: function () {
      return unavailable();
    },
    getCustomerEntryHistory: function () {
      return unavailable();
    },
    getGiveawayStatus: function () {
      return unavailable();
    },
    getMembershipEntitlement: function () {
      return unavailable();
    },
  };

  /*
    Development mirror. Reads the display values the merchant entered in theme
    metafields so the interface can be built and reviewed before an entry
    provider exists. Every result is flagged authoritative: false, and campaign,
    history and entitlement data stay unavailable because no display mirror for
    them exists.

    This provider must not be selected for production.
  */
  const metafieldMirror = {
    id: 'metafield-mirror',

    getProductEntryValue: function (productId) {
      const config = readConfig();
      const values = (config.displayEntryValues || {});
      return mirrored(values[String(productId)], 'theme_metafield');
    },

    getCartLineEntryValue: function (lineKey) {
      const config = readConfig();
      const values = (config.cartLineEntryValues || {});
      return mirrored(values[String(lineKey)], 'theme_metafield');
    },

    /*
      Deliberately unavailable. A cart total is a legally meaningful figure and
      summing display mirrors would produce a number the storefront cannot
      stand behind. Only a real provider may answer this.
    */
    getCartTotalEntries: function () {
      return unavailable('not_derivable_from_display_values');
    },

    getCustomerEntryHistory: function () {
      return unavailable();
    },

    getGiveawayStatus: function () {
      return unavailable();
    },

    getMembershipEntitlement: function () {
      return unavailable();
    },
  };

  const providers = {};
  providers[noProvider.id] = noProvider;
  providers[metafieldMirror.id] = metafieldMirror;

  function activeProvider() {
    const config = readConfig();
    return providers[config.provider] || noProvider;
  }

  const GemdrawAdapter = {
    get providerId() {
      return activeProvider().id;
    },

    /* Lets a future provider module register itself without editing this file. */
    register(provider) {
      if (!provider || !provider.id) return;
      providers[provider.id] = provider;
    },

    getProductEntryValue(productId) {
      return activeProvider().getProductEntryValue(productId);
    },

    getCartLineEntryValue(lineKey) {
      return activeProvider().getCartLineEntryValue(lineKey);
    },

    getCartTotalEntries() {
      return activeProvider().getCartTotalEntries();
    },

    getCustomerEntryHistory() {
      return activeProvider().getCustomerEntryHistory();
    },

    getGiveawayStatus(campaignId) {
      return activeProvider().getGiveawayStatus(campaignId);
    },

    getMembershipEntitlement() {
      return activeProvider().getMembershipEntitlement();
    },
  };

  window.GemdrawAdapter = GemdrawAdapter;
})();
